import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { RestartAlt } from '@mui/icons-material';
import { Box, IconButton, Typography, styled } from '@mui/material';
import { Root, createRoot } from 'react-dom/client';
import { ConfirmationDialog } from '../../ConfirmationDialog';
import React from 'react';
import { AreaCalculationControl } from './AreaCalculationControl';

export class ResetControl implements mapboxgl.IControl {
  private container: HTMLElement;
  private root: Root;
  private map: mapboxgl.Map;
  private draw: MapboxDraw;
  private area: AreaCalculationControl;
  private onReset?: () => void;

  constructor(draw: MapboxDraw, area: AreaCalculationControl, onReset?: () => void) {
    this.container = document.createElement('div');
    this.container.className = 'mapboxgl-ctrl';
    this.root = createRoot(this.container);
    this.draw = draw;
    this.area = area;
    this.onReset = onReset;

    this.render();
  }

  render() {
    this.root.render(<ResetDrawComponent draw={this.draw} area={this.area} onReset={this.onReset} />);
  }

  onAdd(map) {
    this.map = map;
    return this.container;
  }

  onRemove() {
    this.container.parentNode.removeChild(this.container);
    this.map = undefined;
  }
}

const ControlContainer = styled(Box)({
  backgroundColor: 'white',
  borderRadius: '4px',
});

export const ResetDrawComponent: React.FC<{
  draw: MapboxDraw;
  area: AreaCalculationControl;
  onReset?: () => void;
}> = ({ draw, area, onReset }) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);

  const handleConfirm = () => {
    draw.deleteAll();
    area.update(draw.getAll());
    // deleteAll() fires no draw event, so notify the map to clear its AOI state — otherwise the
    // page keeps the previous polygon and Continue stays enabled after a reset.
    onReset?.();
    setConfirmDialogOpen(false);
  };

  return (
    <ControlContainer id="map-reset-control">
      <IconButton
        title="Reset"
        size="small"
        onClick={() => {
          setConfirmDialogOpen(true);
        }}
        sx={{
          '& .MuiSvgIcon-root': { fontSize: '20px', color: 'black' },
        }}
      >
        <RestartAlt />
      </IconButton>
      <ConfirmationDialog
        title="Confirmation"
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onAgree={() => handleConfirm()}
      >
        <Typography>
          You are about to clear the map. Any changes will be lost. Do you wish to continue?
        </Typography>
      </ConfirmationDialog>
    </ControlContainer>
  );
};
